import { Route, Switch } from "wouter";
import { LazyMotion, domAnimation, MotionConfig } from "framer-motion";
import { Landing } from "./pages/Landing";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { Dashboard } from "./pages/Dashboard";
import { ChildNew } from "./pages/ChildNew";
import { ChildDetail } from "./pages/ChildDetail";
import { EmergencyInfoForm } from "./pages/EmergencyInfoForm";
import { CaregiverView } from "./pages/CaregiverView";
import { OrgOnboarding } from "./pages/OrgOnboarding";
import { OrgAdmin } from "./pages/OrgAdmin";
import { IntakeForm } from "./pages/IntakeForm";
import { NotificationCenter } from "./pages/NotificationCenter";
import { NotificationSettings } from "./pages/NotificationSettings";
import { SecuritySettings } from "./pages/SecuritySettings";
import { RegionSettings } from "./pages/RegionSettings";
import { Terms, Privacy } from "./pages/Legal";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { AdminConsole } from "./pages/AdminConsole";
import { NotFound } from "./pages/NotFound";
import { RequireAuth } from "./lib/auth/RequireAuth";
import { IS_DEMO_MODE } from "./lib/api/demoMode";
import { INSTITUTIONS_ENABLED } from "@shared/constants/features";
import { SimPanel } from "./features/_sim/SimPanel"; // SIM-SEAM: simulation controls (demo mode only)
import { MarketSync } from "./features/_sim/MarketSync"; // SIM-SEAM: account market → kit context
import { FeatureSlot } from "./features/_sim/Slot"; // SIM-SEAM: app-wide feature overlays
import { useSimRoutes } from "./features/_sim/routes"; // SIM-SEAM: v2 feature routes
import { IS_SHOTS } from "./features/_sim/params"; // SIM-SEAM: ?shots=1 screenshot mode

function DemoBanner() {
  if (!IS_DEMO_MODE || IS_SHOTS) return null; // SIM-SEAM: ?shots=1 hides the banner for screenshots
  return (
    <div className="no-print sticky top-0 z-50 bg-amber-100/90 px-4 py-2 text-center text-xs font-medium text-amber-900 shadow-sm backdrop-blur">
      Demo mode — API calls are mocked client-side. Data resets on refresh.
    </div>
  );
}

export default function App() {
  const simRoutes = useSimRoutes(); // SIM-SEAM: enabled v2 feature routes
  return (
    <LazyMotion features={domAnimation} strict>
      <MotionConfig reducedMotion="user">
        <DemoBanner />
        {/* SIM-SEAM: floating simulation controls + app-wide overlays (welcome sheet, tour, cart drawer) */}
        <SimPanel />
        <MarketSync /> {/* SIM-SEAM: the account's real market + city (normal builds only) */}
        <FeatureSlot name="app.overlay" />
        <Switch>
          <Route path="/" component={Landing} />
          <Route path="/login" component={Login} />
          <Route path="/register" component={Register} />
          <Route path="/terms" component={Terms} />
          <Route path="/privacy" component={Privacy} />
          <Route path="/forgot-password" component={ForgotPassword} />
          <Route path="/reset-password/:token" component={ResetPassword} />
          {/* /care/:token — PUBLIC caregiver view, OUTSIDE RequireAuth */}
          <Route path="/care/:token" component={CaregiverView} />
          {/* /intake/:token — PUBLIC medical-proxy intake form, OUTSIDE RequireAuth */}
          {INSTITUTIONS_ENABLED && <Route path="/intake/:token" component={IntakeForm} />}
          <Route path="/dashboard">
            <RequireAuth>
              <Dashboard />
            </RequireAuth>
          </Route>
          <Route path="/child/new">
            <RequireAuth>
              <ChildNew />
            </RequireAuth>
          </Route>
          <Route path="/child/:id/eif">
            <RequireAuth>
              <EmergencyInfoForm />
            </RequireAuth>
          </Route>
          <Route path="/child/:id">
            <RequireAuth>
              <ChildDetail />
            </RequireAuth>
          </Route>
          {/* Institutional layer — off, so /org/* falls through to NotFound. */}
          {INSTITUTIONS_ENABLED && (
            <Route path="/org/new">
              <RequireAuth>
                <OrgOnboarding />
              </RequireAuth>
            </Route>
          )}
          {INSTITUTIONS_ENABLED && (
            <Route path="/org/admin">
              <RequireAuth>
                <OrgAdmin />
              </RequireAuth>
            </Route>
          )}
          <Route path="/notifications">
            <RequireAuth>
              <NotificationCenter />
            </RequireAuth>
          </Route>
          <Route path="/settings/notifications">
            <RequireAuth>
              <NotificationSettings />
            </RequireAuth>
          </Route>
          <Route path="/settings/security">
            <RequireAuth>
              <SecuritySettings />
            </RequireAuth>
          </Route>
          <Route path="/settings/region">
            <RequireAuth>
              <RegionSettings />
            </RequireAuth>
          </Route>
          <Route path="/admin">
            <RequireAuth>
              <AdminConsole />
            </RequireAuth>
          </Route>
          {/* SIM-SEAM: v2 feature routes, each inside RequireAuth */}
          {simRoutes}
          <Route component={NotFound} />
        </Switch>
      </MotionConfig>
    </LazyMotion>
  );
}
